import { useEffect, useState } from "react";
import { Shield, Save, Trash2, Download, RefreshCw, Database, LogOut, KeyRound, MapPin, BellRing } from "lucide-react";
import * as XLSX from "xlsx";
import { api, money, trDate } from "../lib/api";
import { PageHeader, StatMini } from "../components/farm/FarmUI";
import PushSetup from "../components/farm/PushSetup";

const SESSION_KEY = "kn_admin_ok";

const COLLECTIONS = [
  { key: "rice_fields", label: "Çeltik tarlaları" },
  { key: "dairy_records", label: "Süt kayıtları" },
  { key: "cattle", label: "Büyükbaş" },
  { key: "sheep_records", label: "Küçükbaş hareketleri" },
  { key: "employees", label: "Çalışanlar" },
  { key: "employee_payments", label: "Maaş / avans ödemeleri" },
  { key: "stock_items", label: "Stok kalemleri" },
  { key: "cheques", label: "Çekler" },
  { key: "finance_records", label: "Kasa hareketleri" },
];

export default function Admin() {
  const [authed, setAuthed] = useState(sessionStorage.getItem(SESSION_KEY) === "1");
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [tab, setTab] = useState("profile");
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState({});
  const [saveMsg, setSaveMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [selection, setSelection] = useState(new Set(COLLECTIONS.map((c) => c.key)));
  const [savedPin, setSavedPin] = useState(sessionStorage.getItem("kn_admin_pin") || "");

  const load = () => {
    api.get("/settings").then(setSettings).catch(() => {});
    api.get("/admin/stats").then(setStats).catch(() => {});
  };

  useEffect(() => {
    if (authed) load();
  }, [authed]);

  const verify = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/verify", { pin });
      sessionStorage.setItem(SESSION_KEY, "1");
      sessionStorage.setItem("kn_admin_pin", pin);
      setSavedPin(pin);
      setAuthed(true);
      setPinError("");
    } catch {
      setPinError("PIN hatalı. Tekrar deneyin.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem("kn_admin_pin");
    setSavedPin("");
    setAuthed(false);
    setPin("");
  };

  const saveSettings = async () => {
    setBusy(true); setSaveMsg("");
    try {
      const updated = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings),
      }).then((r) => r.json());
      setSettings(updated);
      setSaveMsg("Profil kaydedildi ✓");
    } catch {
      setSaveMsg("Kaydedilemedi.");
    } finally {
      setBusy(false);
      setTimeout(() => setSaveMsg(""), 3000);
    }
  };

  const exportOne = async (collection, label) => {
    try {
      const d = await api.get(`/admin/export/${collection}`);
      if (!d.rows.length) {
        alert(`"${label}" boş, aktarılacak veri yok.`);
        return;
      }
      const ws = XLSX.utils.json_to_sheet(d.rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, label.slice(0, 30));
      XLSX.writeFile(wb, `${collection}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch {
      alert("Aktarım başarısız.");
    }
  };

  const exportAll = async () => {
    setBusy(true);
    try {
      const wb = XLSX.utils.book_new();
      let total = 0;
      for (const { key, label } of COLLECTIONS) {
        const d = await api.get(`/admin/export/${key}`);
        if (!d.rows.length) continue;
        const ws = XLSX.utils.json_to_sheet(d.rows);
        XLSX.utils.book_append_sheet(wb, ws, label.slice(0, 30));
        total += d.rows.length;
      }
      if (total === 0) { alert("Aktarılacak veri yok."); setBusy(false); return; }
      XLSX.writeFile(wb, `kemal_namli_ciftligi_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch {
      alert("Aktarım başarısız.");
    } finally {
      setBusy(false);
    }
  };

  const toggleSel = (k) => {
    const s = new Set(selection);
    s.has(k) ? s.delete(k) : s.add(k);
    setSelection(s);
  };

  const runReset = async () => {
    const chosen = Array.from(selection);
    if (chosen.length === 0) { alert("En az bir modül seçin."); return; }
    const labels = COLLECTIONS.filter((c) => selection.has(c.key)).map((c) => c.label).join(", ");
    const confirmText = window.prompt(`Aşağıdaki modüller tamamen silinecek:\n\n${labels}\n\nOnaylamak için PIN'inizi girin:`);
    if (!confirmText) return;
    setResetting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reset`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin: confirmText, collections: chosen }),
      });
      if (!res.ok) throw new Error();
      const d = await res.json();
      alert("Silindi: " + JSON.stringify(d.deleted));
      load();
    } catch {
      alert("Silme başarısız. PIN doğru mu?");
    } finally {
      setResetting(false);
    }
  };

  if (!authed) {
    return (
      <div className="admin-lock">
        <div className="admin-lock-card">
          <div className="admin-lock-icon"><Shield size={26} /></div>
          <h2>Admin Paneli</h2>
          <p>Erişim için 4 haneli PIN girin.</p>
          <form onSubmit={verify} className="admin-lock-form">
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              autoFocus
              data-testid="admin-pin-input"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              placeholder="••••"
            />
            {pinError && <span className="admin-pin-error" data-testid="admin-pin-error">{pinError}</span>}
            <button className="primary-btn full" type="submit" data-testid="admin-pin-submit">
              <KeyRound size={15} /> Aç
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="ADMIN PANELİ"
        title="Yönetim"
        subtitle="Profil ayarları, veri özeti, Excel dışa aktarım ve sistem sıfırlama."
        actions={
          <button className="outline-btn" data-testid="admin-logout" onClick={logout}>
            <LogOut size={15} /> Çıkış
          </button>
        }
      />

      <div className="filter-row">
        <button className={`chip ${tab === "profile" ? "active" : ""}`} data-testid="admin-tab-profile" onClick={() => setTab("profile")}>Profil</button>
        <button className={`chip ${tab === "data" ? "active" : ""}`} data-testid="admin-tab-data" onClick={() => setTab("data")}>Veriler</button>
        <button className={`chip ${tab === "export" ? "active" : ""}`} data-testid="admin-tab-export" onClick={() => setTab("export")}>Excel Aktarım</button>
        <button className={`chip ${tab === "push" ? "active" : ""}`} data-testid="admin-tab-push" onClick={() => setTab("push")}>
          <BellRing size={12} style={{ verticalAlign: -2, marginRight: 4 }} /> Bildirim
        </button>
        <button className={`chip ${tab === "reset" ? "active" : ""}`} data-testid="admin-tab-reset" onClick={() => setTab("reset")}>Sıfırla</button>
      </div>

      {tab === "push" && (
        <div className="panel">
          <h2>iPhone / cihaz bildirimleri</h2>
          <p style={{ color: "var(--muted)", fontSize: 12, marginTop: 6 }}>
            Uygulama kapalıyken de sunucudan doğrudan bildirim gelir. iPhone'da uygulamayı ana ekrana ekledikten sonra abone olun.
          </p>
          <div style={{ marginTop: 16 }}>
            <PushSetup adminPin={savedPin} />
          </div>
        </div>
      )}

      {tab === "profile" && settings && (
        <div className="panel">
          <h2>Çiftlik profili</h2>
          <div className="admin-form">
            <label className="form-field">
              <span>Sahibi</span>
              <input data-testid="admin-owner" value={settings.owner_name || ""} onChange={(e) => setSettings({ ...settings, owner_name: e.target.value })} />
            </label>
            <label className="form-field">
              <span>Çiftlik adı</span>
              <input data-testid="admin-farm" value={settings.farm_name || ""} onChange={(e) => setSettings({ ...settings, farm_name: e.target.value })} />
            </label>
            <div className="form-row">
              <label className="form-field">
                <span>Konum</span>
                <input data-testid="admin-location" value={settings.location || ""} onChange={(e) => setSettings({ ...settings, location: e.target.value })} />
              </label>
              <label className="form-field">
                <span>Toplam dönüm</span>
                <input type="number" step="1" data-testid="admin-donum" value={settings.total_donum || ""} onChange={(e) => setSettings({ ...settings, total_donum: Number(e.target.value) || 0 })} />
              </label>
            </div>
            <label className="form-field">
              <span>Telefon</span>
              <input data-testid="admin-phone" value={settings.phone || ""} onChange={(e) => setSettings({ ...settings, phone: e.target.value })} />
            </label>
            <label className="form-field">
              <span><MapPin size={11} style={{ verticalAlign: -1 }} /> Harita merkezi (Enlem, Boylam)</span>
              <div className="form-row" style={{ marginTop: 4 }}>
                <input type="number" step="0.0001" data-testid="admin-lat" value={settings.map_center?.[0] ?? ""} onChange={(e) => setSettings({ ...settings, map_center: [Number(e.target.value), settings.map_center?.[1] ?? 0] })} />
                <input type="number" step="0.0001" data-testid="admin-lng" value={settings.map_center?.[1] ?? ""} onChange={(e) => setSettings({ ...settings, map_center: [settings.map_center?.[0] ?? 0, Number(e.target.value)] })} />
              </div>
            </label>
            <label className="form-field">
              <span>Not</span>
              <textarea data-testid="admin-note" value={settings.note || ""} onChange={(e) => setSettings({ ...settings, note: e.target.value })} />
            </label>
          </div>
          <div className="admin-save-row">
            <button className="primary-btn" data-testid="admin-save-profile" onClick={saveSettings} disabled={busy}>
              <Save size={15} /> {busy ? "Kaydediliyor..." : "Profili kaydet"}
            </button>
            {saveMsg && <span className={`admin-save-msg ${saveMsg.includes("✓") ? "ok" : "err"}`} data-testid="admin-save-msg">{saveMsg}</span>}
          </div>
        </div>
      )}

      {tab === "data" && (
        <div className="panel">
          <div className="panel-head">
            <div>
              <h2>Veri özeti</h2>
              <p>Her modülde bulunan toplam kayıt sayısı</p>
            </div>
            <button className="outline-btn" data-testid="admin-refresh-stats" onClick={load}>
              <RefreshCw size={14} /> Yenile
            </button>
          </div>
          <div className="admin-stats-grid">
            {COLLECTIONS.map((c) => (
              <div className="admin-stat" key={c.key} data-testid={`admin-stat-${c.key}`}>
                <Database size={16} />
                <div>
                  <small>{c.label}</small>
                  <b>{stats[c.key] || 0}</b>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "export" && (
        <div className="panel">
          <div className="panel-head">
            <div>
              <h2>Excel'e aktarım</h2>
              <p>Muhasebeciye WhatsApp'la göndermek için indirin.</p>
            </div>
            <button className="primary-btn" data-testid="admin-export-all" onClick={exportAll} disabled={busy}>
              <Download size={15} /> {busy ? "Hazırlanıyor..." : "Tümünü indir (tek Excel)"}
            </button>
          </div>
          <div className="admin-export-list">
            {COLLECTIONS.map((c) => (
              <div className="admin-export-row" key={c.key}>
                <div>
                  <b>{c.label}</b>
                  <small>{stats[c.key] || 0} kayıt</small>
                </div>
                <button className="ghost-btn small" data-testid={`admin-export-${c.key}`} onClick={() => exportOne(c.key, c.label)}>
                  <Download size={13} /> İndir
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "reset" && (
        <div className="panel admin-danger">
          <h2>Sistem sıfırlama</h2>
          <p className="admin-warn">
            Seçtiğiniz modüllerin <b>tüm kayıtları silinir</b>. Bu işlem geri alınamaz.
            Önce Excel'e aktardığınızdan emin olun. Silmek için tekrar PIN girmeniz istenecek.
          </p>
          <div className="admin-reset-list">
            {COLLECTIONS.map((c) => (
              <label className="admin-reset-item" key={c.key}>
                <input type="checkbox" data-testid={`admin-reset-check-${c.key}`} checked={selection.has(c.key)} onChange={() => toggleSel(c.key)} />
                <span>{c.label}</span>
                <b>{stats[c.key] || 0}</b>
              </label>
            ))}
          </div>
          <div className="admin-reset-actions">
            <button className="outline-btn" onClick={() => setSelection(new Set())}>Hiçbiri</button>
            <button className="outline-btn" onClick={() => setSelection(new Set(COLLECTIONS.map((c) => c.key)))}>Hepsini seç</button>
            <button className="primary-btn danger" data-testid="admin-reset-run" onClick={runReset} disabled={resetting || selection.size === 0}>
              <Trash2 size={15} /> {resetting ? "Siliniyor..." : `Seçilenleri sil (${selection.size})`}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
